import React, { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { OAuthProvider, signInWithPopup, signOut as fbSignOut, linkWithCredential, type OAuthCredential } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { linkStaffSsoAccount, noteSignIn } from '../lib/functions';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../lib/firebase';

/** Map Firebase auth error codes to friendly, non-technical messages. */
function friendlyAuthError(code: string | undefined): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'Incorrect email or password. Please try again.';
    case 'auth/user-disabled':
      return 'This account has been deactivated. Please contact your school admin.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a few minutes before trying again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    default:
      return 'Sign-in failed. Please try again or contact support.';
  }
}

import { useAuth } from '../hooks/useAuth';
import Logo from '../components/Logo';
import { COMPANY_NAME } from '../lib/company';

type View = 'signIn' | 'forgotPassword';

export default function LoginPage() {
  const { signIn, setLinkingSso } = useAuth();
  const [view, setView] = useState<View>('signIn');

  // Sign-in state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when Microsoft authenticated someone who already has a password
  // account for that email. Firebase will not attach the second identity on
  // its own; they sign in once with their password and we link it.
  const [pendingLink, setPendingLink] = useState<{ cred: OAuthCredential; email: string } | null>(null);

  // Forgot password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      noteSignIn('success', 'password', email);
      if (pendingLink) {
        // They have proved they own the account; attach the Microsoft identity
        // so this is the last time a password is needed.
        try {
          if (auth.currentUser) await linkWithCredential(auth.currentUser, pendingLink.cred);
        } catch (linkErr) {
          console.error('Linking Microsoft to the existing account failed', linkErr);
        }
        setPendingLink(null);
      }
    } catch (err) {
      const code = (err as { code?: string }).code;
      noteSignIn('error', 'password', email, code);
      setError(friendlyAuthError(code));
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setResetBusy(true);
    setResetError(null);
    try {
      const fns = getFunctions(app, 'europe-west2');
      const resetFn = httpsCallable(fns, 'requestPasswordReset');
      await resetFn({ email: resetEmail });
      setResetSent(true);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'functions/invalid-argument') {
        setResetError('Please enter a valid email address.');
      } else {
        setResetError('Something went wrong. Please try again.');
      }
    } finally {
      setResetBusy(false);
    }
  };

  /** Sign in with Microsoft, then match the verified email to a staff record.
   *  A failed match must not leave them holding a signed-in Firebase session
   *  with no role — that reads as a half-broken app — so sign them back out. */
  const handleMicrosoft = async () => {
    setBusy(true);
    setError('');
    // Hold the /login route here until the match is known — see linkingSso.
    setLinkingSso(true);
    try {
      await signInWithPopup(auth, new OAuthProvider('microsoft.com'));
      await linkStaffSsoAccount({});
      // The server has just set role/schoolId claims; fetch a token that
      // carries them before the app's Firestore listeners start.
      await auth.currentUser?.getIdToken(true);
      setLinkingSso(false);
      // AuthProvider picks up the new claims and routes onward.
    } catch (e: unknown) {
      const code = (e as { code?: string; message?: string });
      const raw = code.message ?? '';
      if (raw.includes('NO_MATCH')) {
        setError('We couldn\u2019t find you in your school\u2019s staff list. Ask your school admin to check your work email is in the MIS.');
      } else if (raw.includes('AMBIGUOUS')) {
        setError('That email matches more than one staff record. Please contact your school admin.');
      } else if (raw.includes('SCHOOL_INACTIVE')) {
        setError('Your school\u2019s SchoolOne account is not active. Please contact your school admin.');
      } else if (code.code === 'auth/account-exists-with-different-credential') {
        const cred = OAuthProvider.credentialFromError(e as Parameters<typeof OAuthProvider.credentialFromError>[0]);
        const existing = ((e as { customData?: { email?: string } }).customData?.email) ?? '';
        if (cred && existing) {
          setPendingLink({ cred, email: existing });
          setEmail(existing);
          setError('');
        } else {
          setError('You already have a SchoolOne account for this email. Please sign in with your password.');
        }
      } else if (code.code === 'auth/operation-not-allowed') {
        // The Microsoft provider has not been enabled in Firebase Auth yet.
        setError('Microsoft sign-in isn\u2019t switched on for SchoolOne yet. Please sign in with your email and password.');
      } else if (code.code === 'auth/popup-closed-by-user' || code.code === 'auth/cancelled-popup-request') {
        setError('');
      } else if (code.code?.startsWith('auth/')) {
        // Microsoft refused inside its own popup, so the server never saw this
        // and never will. It is also the failure a member of staff describes as
        // "it said my account doesn't exist", which is why it is worth a row of
        // its own rather than being folded into a generic error.
        noteSignIn('provider_failed', 'microsoft.com',
          (e as { customData?: { email?: string } }).customData?.email, code.code);
        console.error('Microsoft sign-in failed', e);
        setError(
          (friendlyAuthError(code.code) || 'Could not sign you in with Microsoft. Please try again.')
          + ` (${code.code})`,
        );
      } else {
        // Anything unrecognised: keep the message plain, but carry the code so
        // it can be acted on. A bare "sign-in failed" is undiagnosable for a
        // school, and was for us.
        console.error('Microsoft sign-in failed', e);
        const detail = code.code ?? '';
        setError(
          (friendlyAuthError(code.code) || 'Could not sign you in with Microsoft. Please try again.')
          + (detail ? ` (${detail})` : ''),
        );
      }
      // Keep the pending-link case signed out too: linking happens against the
      // password account they are about to sign into, not the Microsoft one.
      await fbSignOut(auth).catch(() => {});
      setLinkingSso(false);
    } finally {
      setBusy(false);
    }
  };

  const switchToForgot = () => {
    // Pre-fill reset email from whatever the user typed in the sign-in form
    setResetEmail(email);
    setResetError(null);
    setResetSent(false);
    setView('forgotPassword');
  };

  const switchToSignIn = () => {
    setError(null);
    setView('signIn');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-900 flex-col justify-between p-12">
        <div>
          <Logo variant="bars" size={40} theme="light" showWordmark />
        </div>
        <div>
          <blockquote className="text-brand-100 text-xl font-light leading-relaxed">
            "One platform for the workflows your staff use every term — reviews, CPD, marking, and leave."
          </blockquote>
        </div>
        <p className="text-brand-400 text-sm">© {new Date().getFullYear()} {COMPANY_NAME} · SchoolOne</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-16 bg-white">
        <div className="mx-auto w-full max-w-sm">
          {/* Logo / wordmark (mobile) */}
          <div className="mb-10 lg:hidden">
            <Logo variant="bars" size={36} theme="dark" showWordmark />
          </div>

          {view === 'signIn' ? (
            <>
              <Link
                to="/"
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-8"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back to home
              </Link>
              <h2 className="text-3xl font-bold text-gray-900 mb-1">Welcome back</h2>
              <p className="text-sm text-gray-500 mb-8">Sign in to your school account</p>

              {pendingLink && (
                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  <p className="font-semibold">You already have a SchoolOne account</p>
                  <p className="mt-1">
                    <strong>{pendingLink.email}</strong> was set up with a password. Enter it once
                    below and we&rsquo;ll connect your Microsoft account &mdash; after that you can
                    use the Microsoft button every time.
                  </p>
                </div>
              )}

              <form onSubmit={(e) => void handleSignIn(e)} className="space-y-5">
                <div>
                  <label htmlFor="email" className="label">Email address</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@school.ac.uk"
                    className="input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="password" className="label mb-0">Password</label>
                    <button
                      type="button"
                      onClick={switchToForgot}
                      className="text-xs text-brand-600 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="input"
                  />
                </div>

                {error && (
                  <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary w-full py-2.5"
                >
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium text-gray-400">or</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              <button
                type="button"
                disabled={busy}
                onClick={() => void handleMicrosoft()}
                className="btn-secondary w-full py-2.5 flex items-center justify-center gap-2.5 disabled:opacity-50"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 21 21" aria-hidden="true">
                  <rect x="1"  y="1"  width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1"  width="9" height="9" fill="#7fba00" />
                  <rect x="1"  y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                {busy ? 'Signing in…' : 'Sign in with Microsoft'}
              </button>
              <p className="mt-3 text-xs text-gray-400 text-center">
                Use your school Microsoft account. Your access is matched to your school&rsquo;s staff record.
              </p>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={switchToSignIn}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-8"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back to sign in
              </button>

              <h2 className="text-3xl font-bold text-gray-900 mb-1">Reset password</h2>
              <p className="text-sm text-gray-500 mb-8">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {resetSent ? (
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-4 text-sm text-green-700 space-y-1">
                  <p className="font-medium">Check your inbox</p>
                  <p>If an account exists for <strong>{resetEmail}</strong>, a password reset link has been sent. Check your spam folder if it doesn't arrive within a few minutes.</p>
                  <button
                    type="button"
                    onClick={switchToSignIn}
                    className="mt-3 text-brand-600 hover:underline text-sm font-medium"
                  >
                    Return to sign in →
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => void handleForgotPassword(e)} className="space-y-5">
                  <div>
                    <label htmlFor="reset-email" className="label">Email address</label>
                    <input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      autoComplete="email"
                      placeholder="you@school.ac.uk"
                      className="input"
                      autoFocus
                    />
                  </div>

                  {resetError && (
                    <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                      {resetError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={resetBusy}
                    className="btn-primary w-full py-2.5"
                  >
                    {resetBusy ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        {/* Footer links */}
        <div className="mt-10 text-center text-xs text-gray-400 space-x-4">
          <Link to="/privacy" className="hover:text-gray-600 hover:underline">Privacy Policy</Link>
          <span>·</span>
          <Link to="/cookies" className="hover:text-gray-600 hover:underline">Cookie Policy</Link>
          <span>·</span>
          <span>© {new Date().getFullYear()} SchoolOne</span>
        </div>
      </div>
    </div>
  );
}
